import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function QuotationsPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState('');
  const [address, setAddress] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const services = [
    { id: 'residential', name: 'Residential Demolition', icon: 'ri-home-4-line' },
    { id: 'commercial', name: 'Commercial Demolition', icon: 'ri-building-line' },
    { id: 'arborist', name: 'Consultation on Arborist Report', icon: 'ri-plant-line' },
    { id: 'impact', name: 'Consultation on Impact Assessment Report', icon: 'ri-file-chart-line' },
    { id: 'land', name: 'Consultation on Land Assessment Report', icon: 'ri-map-pin-line' },
    { id: 'soil', name: 'Consultation on Soil / Geotechnical Test', icon: 'ri-flask-line' },
  ];

  const handleNext = () => {
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleFileUpload = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('service', selectedService);
      formData.append('address', address);
      formData.append('name', name);
      formData.append('phone', phone);
      formData.append('email', email);
      
      uploadedFiles.forEach((file, index) => {
        formData.append(`photo_${index + 1}`, file);
      });

      const response = await fetch('https://readdy.ai/api/form/d5pm1e0tu5df1co4cvqg', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStep1Valid = selectedService !== '';
  const isStep2Valid = address.trim() !== '';
  const isStep3Valid = name.trim() !== '' && phone.trim() !== '' && email.trim() !== '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-charcoal via-charcoal-light to-charcoal-dark overflow-hidden pt-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-indigo-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-20">
          <h1 className="font-heading text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Get Your Free Quote
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Complete our simple 5-step form and receive competitive quotes from licensed professionals
          </p>
        </div>
      </section>

      {/* Multi-Step Form Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          {!isSubmitted ? (
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Progress Indicator */}
              <div className="bg-charcoal px-8 py-6">
                <div className="flex items-center justify-between">
                  {[1, 2, 3, 4, 5].map((step) => (
                    <div key={step} className="flex items-center">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                          step < currentStep
                            ? 'bg-green-500 text-white'
                            : step === currentStep
                            ? 'bg-indigo-500 text-white'
                            : 'bg-gray-700 text-gray-400'
                        }`}
                      >
                        {step < currentStep ? (
                          <i className="ri-check-line text-xl"></i>
                        ) : (
                          step
                        )}
                      </div>
                      {step < 5 && (
                        <div
                          className={`w-16 h-1 mx-2 transition-all ${
                            step < currentStep ? 'bg-green-500' : 'bg-gray-700'
                          }`}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-center">
                  <p className="text-gray-300 text-sm">
                    Step {currentStep} of 5
                  </p>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-10">
                {/* Step 1: Service Selection */}
                {currentStep === 1 && (
                  <div className="transition-all duration-300">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-3 text-center">
                      Select Your Service
                    </h2>
                    <p className="text-gray-600 text-center mb-8">
                      Choose the service you need for your property
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {services.map((service) => (
                        <button
                          key={service.id}
                          onClick={() => setSelectedService(service.name)}
                          className={`p-6 rounded-xl border-2 transition-all cursor-pointer text-left hover:shadow-lg ${
                            selectedService === service.name
                              ? 'border-green-500 bg-green-50 shadow-lg shadow-green-500/20'
                              : 'border-gray-200 hover:border-indigo-300'
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div
                              className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                selectedService === service.name
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              <i className={`${service.icon} text-2xl`}></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900 mb-1">
                                {service.name}
                              </h3>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2: Property Address */}
                {currentStep === 2 && (
                  <div className="transition-all duration-300">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-3 text-center">
                      Property Address
                    </h2>
                    <p className="text-gray-600 text-center mb-8">
                      Enter the full address of your property
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Address
                      </label>
                      <input
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="123 Main Street, City, State, ZIP"
                        className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-all text-base"
                      />
                    </div>
                  </div>
                )}

                {/* Step 3: Contact Details */}
                {currentStep === 3 && (
                  <div className="transition-all duration-300">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-3 text-center">
                      Contact Details
                    </h2>
                    <p className="text-gray-600 text-center mb-8">
                      How can we reach you?
                    </p>
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="John Doe"
                          className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-all text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="(555) 123-4567"
                          className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-all text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="john@example.com"
                          className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-all text-base"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Photo Upload */}
                {currentStep === 4 && (
                  <div className="transition-all duration-300">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-3 text-center">
                      Upload Site Photos
                    </h2>
                    <p className="text-gray-600 text-center mb-8">
                      Add photos of your property (optional)
                    </p>
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer ${
                        isDragging
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <i className="ri-upload-cloud-2-line text-3xl text-gray-400"></i>
                      </div>
                      <p className="text-gray-700 font-medium mb-2">
                        Drag and drop your photos here
                      </p>
                      <p className="text-gray-500 text-sm mb-4">or</p>
                      <label className="inline-block px-6 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-all cursor-pointer whitespace-nowrap">
                        Browse Files
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e.target.files)}
                          className="hidden"
                        />
                      </label>
                    </div>

                    {uploadedFiles.length > 0 && (
                      <div className="mt-6">
                        <p className="text-sm font-medium text-gray-700 mb-3">
                          Uploaded Photos ({uploadedFiles.length})
                        </p>
                        <div className="grid grid-cols-3 gap-3">
                          {uploadedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="relative group rounded-lg overflow-hidden border-2 border-gray-200"
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`Upload ${index + 1}`}
                                className="w-full h-32 object-cover"
                              />
                              <button
                                onClick={() => removeFile(index)}
                                className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                              >
                                <i className="ri-close-line"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 5: Review & Submit */}
                {currentStep === 5 && (
                  <div className="transition-all duration-300">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-3 text-center">
                      Review Your Request
                    </h2>
                    <p className="text-gray-600 text-center mb-8">
                      Please confirm your details before submitting
                    </p>
                    <div className="space-y-4">
                      <div className="p-5 bg-gray-50 rounded-xl">
                        <p className="text-sm font-medium text-gray-500 mb-1">
                          Selected Service
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                          {selectedService}
                        </p>
                      </div>
                      <div className="p-5 bg-gray-50 rounded-xl">
                        <p className="text-sm font-medium text-gray-500 mb-1">
                          Property Address
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                          {address}
                        </p>
                      </div>
                      <div className="p-5 bg-gray-50 rounded-xl">
                        <p className="text-sm font-medium text-gray-500 mb-1">
                          Contact Information
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                          {name}
                        </p>
                        <p className="text-sm text-gray-600">{phone}</p>
                        <p className="text-sm text-gray-600">{email}</p>
                      </div>
                      <div className="p-5 bg-gray-50 rounded-xl">
                        <p className="text-sm font-medium text-gray-500 mb-1">
                          Uploaded Photos
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                          {uploadedFiles.length} photo(s) uploaded
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-200">
                  {currentStep > 1 && (
                    <button
                      onClick={handleBack}
                      className="px-6 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      Back
                    </button>
                  )}
                  {currentStep < 5 ? (
                    <button
                      onClick={handleNext}
                      disabled={
                        (currentStep === 1 && !isStep1Valid) ||
                        (currentStep === 2 && !isStep2Valid) ||
                        (currentStep === 3 && !isStep3Valid)
                      }
                      className={`ml-auto px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                        (currentStep === 1 && !isStep1Valid) ||
                        (currentStep === 2 && !isStep2Valid) ||
                        (currentStep === 3 && !isStep3Valid)
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-indigo-500 text-white hover:bg-indigo-600 cursor-pointer'
                      }`}
                    >
                      Next
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="ml-auto px-8 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                      <i className="ri-send-plane-fill ml-2"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-3xl shadow-2xl p-16 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-check-line text-5xl text-green-500"></i>
              </div>
              <h2 className="font-heading text-4xl font-bold text-gray-900 mb-4">
                Request Submitted!
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                Thank you for your request. Our team will review your information and get back to you shortly with competitive quotes.
              </p>
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setCurrentStep(1);
                  setSelectedService('');
                  setAddress('');
                  setName('');
                  setPhone('');
                  setEmail('');
                  setUploadedFiles([]);
                }}
                className="px-8 py-3 bg-indigo-500 text-white rounded-lg font-semibold hover:bg-indigo-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Submit Another Request
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading text-4xl font-black text-gray-900 mb-16 text-center leading-tight">
            Why Choose DemoHub?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Licensed Professionals
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                All contractors in our network are fully licensed, insured, and verified for your peace of mind.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-4xl text-indigo-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Competitive Pricing
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Receive multiple quotes and compare prices to ensure you get the best value for your project.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Fast Response
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Get quotes within 24-48 hours and start your demolition project without unnecessary delays.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}